/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */

import { Button, Checkbox, Divider, Flex, Group, Stack, Switch, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconMenu2 } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import DraggableList from 'react-draggable-list';

import { deleteIcon } from '@assets';
import { AsyncAutocomplete, NumberInput, Select, TextInput } from '@components';
import {
  buildString,
  constants,
  createInitialState,
  deconstructString,
  MODE_OPTIONS,
  TemplateFieldProps,
  TemplateFieldState,
  TYPE_OPTIONS,
} from './templateFieldUtils';

export const TemplateField: React.FC<TemplateFieldProps> = ({
  form,
  arrayPath,
  disabled = false,
  allowManualOrdering = false,
  maxRows = constants.DEFAULT_MAX_ROWS,
  onChange,
  propertyOptions = [],
}) => {
  const theme = useMantineTheme();
  const [arrayPathValue, setArrayPathValue] = useState([]);
  const [reviewOrder, setReviewOrder] = useState(false);

  const addRow = () => {
    const array = [...arrayPathValue, createInitialState()];
    setArrayPathValue(array);
    handleChange(array);
  };

  const removeRow = (rowIdx: number) => {
    const array = [...arrayPathValue];
    array.splice(rowIdx, 1);
    setArrayPathValue(array);
    handleChange(array);
  };

  const updateField = (rowIdx: number, updates: Partial<TemplateFieldState>) => {
    const array = [...arrayPathValue];
    array[rowIdx] = { ...array[rowIdx], ...updates };
    setArrayPathValue(array);
    handleChange(array);
  };

  const updateFieldsOrder = (newArrayString: string[]) => {
    const newArray = newArrayString.map((string) => deconstructString(string));
    setArrayPathValue(newArray);
    handleChange(newArray);
  };

  const handleChange = (array) => {
    if (onChange) {
      const strings = (array || []).map((field: TemplateFieldState) => buildString(field));
      onChange(strings, arrayPath);
    }
  };

  useEffect(() => {
    setArrayPathValue((form.getInputProps(arrayPath).value ?? []).map((string) => deconstructString(string)));
  }, []);

  const renderInputField = (field: TemplateFieldState, rowIdx: number) => {
    return (
      <Stack p="md" key={`field-${rowIdx}`}>
        <Group align="center">
          <Select
            label="Field Type"
            data={MODE_OPTIONS}
            value={field.mode || 'mapping'}
            onChange={(value) => updateField(rowIdx, { mode: value as any })}
            disabled={disabled}
            w={180}
          />
          {field.mode === 'mapping' && (
            <Checkbox label="Ignore" checked={field.ignore} disabled={disabled} onChange={(e) => updateField(rowIdx, { ignore: e.currentTarget.checked })} />
          )}

          {!field.ignore && field.mode === 'mapping' && (
            <>
              <Checkbox label="Trim" checked={field.trim} onChange={(e) => updateField(rowIdx, { trim: e.currentTarget.checked })} disabled={disabled} />
              <Checkbox
                label="WCOFormat"
                checked={field.wcoFormat}
                onChange={(e) => updateField(rowIdx, { wcoFormat: e.currentTarget.checked })}
                disabled={disabled}
              />
              <Checkbox
                label="Decimal"
                checked={field.hasDecimal}
                onChange={(e) => {
                  if (!e.currentTarget.checked) {
                    updateField(rowIdx, { hasDecimal: false, autoRound: false, decimal: null });
                  } else {
                    updateField(rowIdx, { hasDecimal: true, decimal: 1 });
                  }
                }}
                disabled={disabled}
              />
              <Checkbox
                label="Auto round"
                checked={field.autoRound}
                onChange={(e) => {
                  if (e.currentTarget.checked) {
                    updateField(rowIdx, { autoRound: true, hasDecimal: true, decimal: 1 });
                  } else {
                    updateField(rowIdx, { autoRound: false, hasDecimal: false, decimal: null });
                  }
                }}
                disabled={disabled}
              />
              <Checkbox
                label="Optional"
                checked={field.optional}
                onChange={(e) => updateField(rowIdx, { optional: e.currentTarget.checked })}
                disabled={disabled}
              />
              <Checkbox
                label="Default value"
                checked={field.hasDefaultValue}
                onChange={(e) => updateField(rowIdx, { hasDefaultValue: e.currentTarget.checked, defaultValue: '' })}
                disabled={disabled}
              />
            </>
          )}
          {!disabled && (
            <UnstyledButton ml={20} w={50} h={50} type="button" onClick={() => removeRow(rowIdx)}>
              <img src={deleteIcon} alt="Remove" />
            </UnstyledButton>
          )}
        </Group>

        <Group align="end">
          <TextInput
            label={MODE_OPTIONS.find((option) => option.value === field.mode)?.fieldLabel}
            value={field.fileColumnName}
            onChange={(e) => updateField(rowIdx, { fileColumnName: e.currentTarget.value })}
            disabled={disabled}
            w={field.mode === 'mapping' ? 160 : 300}
          />
          {!field.ignore && (
            <>
              {field.mode === 'mapping' ? (
                <AsyncAutocomplete
                  label="Property"
                  data={propertyOptions || []}
                  value={field.property}
                  onChange={(value) => updateField(rowIdx, { property: value || '' })}
                  w={300}
                  size="lg"
                  disabled={disabled}
                />
              ) : (
                <TextInput
                  label="Variable Name"
                  value={field.property}
                  onChange={(e) => updateField(rowIdx, { property: e.currentTarget.value })}
                  disabled={disabled}
                  w={300}
                />
              )}
            </>
          )}
          {!field.ignore && field.mode === 'mapping' && (
            <>
              <Select
                label="Type"
                data={TYPE_OPTIONS}
                value={field.type}
                onChange={(value) => updateField(rowIdx, { type: value || 'an' })}
                disabled={disabled}
                w={190}
                allowDeselect={false}
              />
              <NumberInput
                label="Min"
                value={field.min}
                onChange={(value) => updateField(rowIdx, { min: typeof value === 'number' ? value : 0 })}
                disabled={disabled}
                w={100}
                min={0}
              />
              <NumberInput
                label="Max"
                value={field.max}
                onChange={(value) => updateField(rowIdx, { max: typeof value === 'number' ? value : 1 })}
                disabled={disabled}
                w={100}
                min={1}
              />
              {field.hasDecimal && (
                <NumberInput
                  label="Decimal"
                  value={field.decimal}
                  onChange={(value) => updateField(rowIdx, { decimal: typeof value === 'number' ? value : undefined })}
                  disabled={disabled}
                  w={120}
                  min={0}
                />
              )}
              {field.hasDefaultValue && (
                <TextInput
                  label="Default value"
                  value={field.defaultValue}
                  onChange={(e) => updateField(rowIdx, { defaultValue: e.currentTarget.value })}
                  disabled={disabled}
                  w={120}
                />
              )}
            </>
          )}
        </Group>
      </Stack>
    );
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const Item = ({ item, dragHandleProps }) => {
    return (
      <Flex align="center" gap={16} h={32}>
        <IconMenu2 color={theme.colors[theme.primaryColor][6]} {...dragHandleProps} cursor="move" />
        {item.value}
      </Flex>
    );
  };

  return (
    <>
      {!disabled && allowManualOrdering && (
        <Switch checked={reviewOrder} label="Review order" mb={reviewOrder ? 32 : 16} onChange={() => setReviewOrder(!reviewOrder)} />
      )}
      {reviewOrder && (
        <div ref={containerRef}>
          <DraggableList
            itemKey="key"
            template={Item as any}
            list={(form.getInputProps(arrayPath).value ?? []).map((value: string, rowIdx: number) => ({
              key: `${arrayPathValue.length}-${rowIdx}`,
              value,
            }))}
            onMoveEnd={(newList) => updateFieldsOrder(newList.map((i: any) => i.value))}
            container={() => containerRef.current}
          />
        </div>
      )}
      {!reviewOrder &&
        arrayPathValue.map((field: TemplateFieldState, rowIdx: number) => (
          <div key={`${arrayPathValue.length}-${rowIdx}`}>
            {renderInputField(field, rowIdx)}
            {rowIdx < arrayPathValue.length - 1 && <Divider size={0} w="100%" style={{ borderBottom: '1px solid #ccc' }} />}
          </div>
        ))}
      {!disabled && !reviewOrder && maxRows > arrayPathValue.length && (
        <Flex pl="md" pt="xs" justify={'flex-start'}>
          <Button type="button" onClick={addRow} variant="outline" size="lg">
            Add field
          </Button>
        </Flex>
      )}
    </>
  );
};
